// ── Plaid Link (loaded from the CDN on demand — no npm dependency in the SPA) ──
//
// Shared by ConnectedAccounts (starts a Link flow) and PlaidOAuthReturn (resumes a
// Link flow after an OAuth institution redirects the browser back to us).
//
// OAuth institutions (e.g. Charles Schwab) don't complete inside the Link iframe: Plaid
// redirects the whole browser tab to our registered `redirect_uri` (`/plaid-oauth`) with
// an `?oauth_state_id=...`, and the SPA must re-create Link there with the SAME link
// token plus `receivedRedirectUri: window.location.href`. Because a full-page navigation
// wipes JS state, the in-flight link token + flow are stashed in sessionStorage before
// Link opens and read back on the return page. Non-OAuth institutions never leave the
// page, so their `onSuccess` fires in place and the stashed record is just cleared.

export interface PlaidLinkHandler { open(): void; exit(): void; destroy(): void }
export interface PlaidLinkOnSuccessMetadata { institution?: { name?: string } | null }

/**
 * The error Plaid passes to `onExit` when Link terminates on a FAILURE — `null` on a
 * genuine user cancel, which is the one case callers should stay silent for. Mirrors
 * Plaid's documented Link error shape. See F3 (appcore docs/29-onboarding-friction-
 * log.md): a real prod payload had `display_message: null`, so `plaidExitErrorMessage`
 * below builds its own message from `error_code` rather than assuming `display_message`
 * is ever populated — and deliberately never reads `display_message` at all, even when
 * present, since it's a field Plaid can change unilaterally without our review.
 */
export interface PlaidLinkExitError {
  error_type?: string;
  error_code?: string;
  error_message?: string;
  display_message?: string | null;
  request_id?: string;
  suggested_action?: string | null;
}

/** Metadata accompanying every `onExit` call, on both the success and failure paths. */
export interface PlaidLinkExitMetadata {
  institution?: { name?: string; institution_id?: string } | null;
  status?: string;
  request_id?: string;
  link_session_id?: string;
}

export interface PlaidLinkConfig {
  token: string;
  onSuccess: (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => void;
  onExit?: (err: PlaidLinkExitError | null, metadata: PlaidLinkExitMetadata) => void;
  /** Set ONLY when resuming after an OAuth redirect — the full return URL incl. query. */
  receivedRedirectUri?: string;
}
declare global {
  interface Window { Plaid?: { create(config: PlaidLinkConfig): PlaidLinkHandler } }
}

/**
 * Plain-language copy for Link exit errors we can anticipate by `error_code` (F3).
 * These are Plaid developer/operator strings translated into what a family member
 * should actually read: whether the connection failed, whether it's worth retrying,
 * and that support is available. NEVER put a URL, an operator instruction (e.g.
 * "configure X in the dashboard"), or Plaid-internal vocabulary in any of these —
 * that diagnostic detail belongs in `logPlaidExitError` below, not here.
 *
 * The two entries below are real prod payloads (see the class docblock above), and
 * both are OUR OWN provisioning gaps, not anything transient on the institution's
 * side, so neither entry promises self-resolution or suggests trying again;
 * contacting us is the only useful next step for either. Full account: appcore
 * docs/29-onboarding-friction-log.md F3.
 *   - INSTITUTION_REGISTRATION_REQUIRED: OUR Plaid client, not this institution, is
 *     the thing that isn't fully registered yet — remediation is on our side, at
 *     the Plaid dashboard's OAuth-institutions status page, and for institutions
 *     like Schwab and PNC it can require additional MANUAL registration steps
 *     that will never complete just by waiting. (The one case that genuinely
 *     clears on its own — a recent Production access grant still propagating —
 *     is bounded to 24 hours, not an indefinite wait, and isn't the case to
 *     write the default message for.)
 *   - INVALID_LINK_CUSTOMIZATION: a Plaid dashboard configuration problem on our
 *     side, not something the user did or can fix themselves.
 * The rest cover the other Link-exit failures we can reasonably expect to see in
 * practice; add to this map as new real `error_code`s show up in the logs rather
 * than guessing ahead of them.
 */
const PLAID_EXIT_MESSAGES: Record<string, string> = {
  INSTITUTION_REGISTRATION_REQUIRED:
    "We're still getting this institution fully set up on our end. Please contact us and we'll take care of it.",
  INVALID_LINK_CUSTOMIZATION:
    "We couldn't complete the connection because of a setup issue on our end, not anything you did. Please contact us and we'll get it sorted out.",
  ITEM_LOGIN_REQUIRED:
    "We couldn't confirm your login with this institution. Please try connecting again and enter your credentials once more.",
  INSTITUTION_DOWN:
    "This institution's connection service is temporarily unavailable. Please try again in a little while.",
  INSTITUTION_NOT_RESPONDING:
    "This institution isn't responding right now. Please try again in a little while.",
  INSTITUTION_NO_LONGER_SUPPORTED:
    "We're no longer able to connect to this institution directly. Please contact us and we'll help you find another way to add these accounts.",
  RATE_LIMIT_EXCEEDED:
    "There were too many attempts in a short time. Please wait a few minutes and try again.",
  INTERNAL_SERVER_ERROR:
    "Something went wrong on our end while completing the connection. Please try again, or contact us if it continues.",
};

/** Shown when `error_code` isn't one we've curated a specific message for. */
const GENERIC_EXIT_MESSAGE =
  "We couldn't complete the connection. Please try again, or contact us if it continues.";

/**
 * A plain-language message for a Link exit error (F3), safe to show a family
 * member: never a URL, an operator instruction, or Plaid-internal vocabulary.
 * Deliberately does NOT read `err.display_message`, even though Plaid documents
 * that field as meant to be user-facing: in every real payload we've captured
 * it's `null` (so the branch has never actually fired and has no proven
 * benefit), and it's a field Plaid can change unilaterally without our review —
 * whereas the curated map below is entirely ours to vet. Zero residual risk
 * beats a guard (e.g. checking for a URL) that would need to keep being
 * hardened against whatever Plaid decides to put in that field next. The raw
 * `error_message` is, likewise, deliberately never surfaced here; see
 * `logPlaidExitError` for where that diagnostic detail goes instead.
 */
export function plaidExitErrorMessage(err: PlaidLinkExitError): string {
  if (err.error_code && PLAID_EXIT_MESSAGES[err.error_code]) return PLAID_EXIT_MESSAGES[err.error_code];
  return GENERIC_EXIT_MESSAGE;
}

/**
 * Logs a Link exit failure's diagnostic fields to the browser console so a support
 * conversation has something to go on (F3). Deliberately narrow: never logs the
 * public token, an access token, or any other credential material — only the error
 * code/message/request id Plaid supplied and the institution's display name.
 */
export function logPlaidExitError(err: PlaidLinkExitError, metadata: PlaidLinkExitMetadata): void {
  console.error('Plaid Link exited with an error', {
    error_code: err.error_code,
    error_message: err.error_message,
    request_id: err.request_id,
    institution: metadata.institution?.name,
  });
}

const PLAID_SCRIPT = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
let scriptPromise: Promise<void> | null = null;

export function loadPlaid(): Promise<void> {
  if (window.Plaid) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = PLAID_SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { scriptPromise = null; reject(new Error('Could not load Plaid Link')); };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// ── Cross-redirect handoff (sessionStorage: same-tab, cleared on tab close) ──

const INFLIGHT_KEY = 'plaid.oauth.inflight';
const COMPLETION_KEY = 'plaid.oauth.completion';
const EXIT_ERROR_KEY = 'plaid.oauth.exitError';

/**
 * The Link flow in progress, stashed before `open()` so `/plaid-oauth` can resume it.
 *
 * `returnTo` is the in-app path the flow STARTED from, so the OAuth return page can put
 * the user back where they were instead of always landing them on Settings → Connections
 * (steward-app#579). Optional because a record written before this field existed can
 * still be sitting in a live tab's sessionStorage across a deploy; `/plaid-oauth` falls
 * back to the connections page when it's absent, which is exactly the old behaviour.
 *
 * `workspaceId` is the workspace the flow was STARTED in, and it is the reason this field
 * lives on the in-flight record rather than only on the completion (steward-app#864).
 * `/plaid-oauth` sits deliberately OUTSIDE WorkspaceProvider (see router.tsx), so the
 * return page has no active workspace of its own to stamp — and the only thing it could
 * reach for, `localStorage`'s `steward-app:workspaceId`, is precisely the value a second
 * tab may have rewritten while the browser was away at the institution. Capturing it at
 * `open()` time, inside the provider, is the one moment the answer is not in doubt; the
 * return page then copies it onto the completion verbatim.
 *
 * Optional for the same deploy reason as `returnTo`: a record written before this field
 * existed can be sitting in a live tab. Its absence is NOT treated as "fine", though —
 * see `completionMatchesWorkspace`, which fails closed on it.
 */
export type PlaidInflight =
  // `quote` (steward-app#2231): the price the person agreed to before Link opened, which the
  // exchange sends so the façade can confirm it. Typed `unknown` because it comes back out of
  // storage; `useConnectAccount` reads it through `parseQuote`, and a missing or unreadable one
  // is sent as no quote, which the façade answers with the current price to confirm again.
  | { kind: 'connect'; linkToken: string; returnTo?: string; workspaceId?: string; quote?: unknown }
  | { kind: 'relink'; linkToken: string; connId: string; returnTo?: string; workspaceId?: string };

/**
 * A finished OAuth Link flow, handed from `/plaid-oauth` back to ConnectedAccounts.
 *
 * `workspaceId` is copied straight off the in-flight record (above) and is what binds a
 * `connect` completion to one family. It matters more here than anywhere else in the
 * Plaid flow because a Plaid public token is not workspace-bound: `POST /connections`
 * carries no id belonging to the starting family, so appcore has nothing to refuse and
 * will happily provision the bank item into whatever `X-Workspace-Id` is ambient when
 * the exchange runs. Every other cross-workspace write in the app fails closed at the
 * server; this one can only be stopped here (steward-app#864).
 */
export type PlaidCompletion =
  | { kind: 'connect'; publicToken: string; institutionName: string; workspaceId?: string; quote?: unknown }
  | { kind: 'relink'; connId: string; workspaceId?: string };

function readJson<T>(store: Storage, key: string): T | null {
  try {
    const raw = store.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null; // malformed or storage blocked (private browsing) — treat as absent
  }
}

function writeJson(store: Storage, key: string, value: unknown): void {
  try { store.setItem(key, JSON.stringify(value)); } catch { /* storage blocked — ignore */ }
}

function remove(store: Storage, key: string): void {
  try { store.removeItem(key); } catch { /* ignore */ }
}

export function saveInflight(v: PlaidInflight): void { writeJson(sessionStorage, INFLIGHT_KEY, v); }
export function readInflight(): PlaidInflight | null { return readJson<PlaidInflight>(sessionStorage, INFLIGHT_KEY); }
export function clearInflight(): void { remove(sessionStorage, INFLIGHT_KEY); }

export function saveCompletion(v: PlaidCompletion): void { writeJson(sessionStorage, COMPLETION_KEY, v); }

/**
 * Read the pending completion WITHOUT consuming it. There is now more than one page
 * that picks completions up (Settings → Connections and Portfolio → Accounts, #579),
 * and only one of them can service a `relink` — so a consumer looks first and clears
 * only what it can actually handle. Dropping a completion it can't finish would lose a
 * connection the user has already authenticated (and, on the connect path, already
 * agreed to be billed for).
 *
 * EVERY consumer must run `completionMatchesWorkspace` (below) before acting on what
 * this returns, and must `clearCompletion()` a record that fails it (steward-app#864).
 * There is deliberately no read-and-remove convenience wrapper any more: the one that
 * used to live here (`takeCompletion`) had no production caller, and next to the check
 * it read like the sanctioned entry point while being the only exported way to consume
 * a completion WITHOUT the check.
 */
export function peekCompletion(): PlaidCompletion | null {
  return readJson<PlaidCompletion>(sessionStorage, COMPLETION_KEY);
}
export function clearCompletion(): void { remove(sessionStorage, COMPLETION_KEY); }

/**
 * The refusal copy for a Plaid flow that was started in one workspace and came back in
 * another (steward-app#864). Four strings, because two things vary and both change what
 * is true:
 *
 *  - WHAT WE KNOW. When the record names a workspace we can say so. When it names none
 *    we cannot, and telling someone they "started in a different workspace" when we
 *    genuinely cannot tell would be a claim we can't back.
 *  - WHAT THEY SHOULD DO NEXT. A connect ends at "Connect an account"; a reconnect ends
 *    at that connection's "Reconnect" button, and nothing was disconnected in the
 *    meantime. Sending a relink user to "connect the account again" would add a second
 *    connection to the family they are trying to repair.
 *
 * The user really did authenticate at their bank, so every one of these has to be honest
 * about what came of it: a silent drop reads as the connection having worked. For a connect,
 * nothing did, and the copy says so. For Manage connection it is NOT "nothing": Plaid applies
 * an account deselection on its side when the user finishes Link, before we ever see the
 * completion (steward-app#2641). So the relink pair says only what we can vouch for, that we
 * stopped before updating Steward, and never "Nothing was changed".
 *
 * Says "workspace" because that is the word the switcher chip and its aria-label use.
 * No em/en dashes (CLAUDE.md content rules).
 */
export const CONNECT_WRONG_WORKSPACE_MESSAGE =
  'You started connecting this account in a different workspace, so we stopped instead of finishing it here. ' +
  'Nothing was connected. Please switch back to the workspace you started in and connect the account again.';

export const CONNECT_UNKNOWN_WORKSPACE_MESSAGE =
  'We could not confirm which workspace this connection was started in, so we stopped instead of finishing it. ' +
  'Nothing was connected. Please connect the account again.';

export const RELINK_WRONG_WORKSPACE_MESSAGE =
  'You started managing this connection in a different workspace, so we stopped before updating Steward here. ' +
  'Please switch back to that workspace and choose Manage connection there.';

export const RELINK_UNKNOWN_WORKSPACE_MESSAGE =
  'We could not confirm the workspace where this change to the connection began, so we stopped before updating Steward. ' +
  'Please choose Manage connection again.';

/**
 * Whether a flow that started under `startedIn` may still finish now that `active` is
 * the workspace every request will be stamped with.
 *
 * A strict comparison, `null === null` INCLUDED, and that inclusion is the point. A null
 * active workspace is a supported mode, not only an error state: the façade falls back
 * to its deploy default when no `X-Workspace-Id` is sent (see `api/client.ts`), and
 * `useCanWrite` deliberately fails OPEN in that state, so the Connect button is on
 * screen. Refusing `null → null` would mean a user in that mode authenticates at their
 * bank and is then told to "switch back to the workspace you started in", which is both
 * untrue and unactionable. Nothing is at risk there: the header was absent at the click
 * and is absent now, so the exchange lands exactly where it was always going to.
 *
 * `null → 'ws-b'` and `'ws-a' → null` are both refused, because in both the header moved.
 *
 * NOT the same rule as {@link completionMatchesWorkspace}, deliberately. This one governs
 * a value held in memory for the life of ONE page, where "unknown" and "unknown" really
 * are the same unknown. That one governs a value read back out of storage after a full
 * page navigation, where they are not.
 */
export function startedInActiveWorkspace(
  startedIn: string | null | undefined,
  active: string | null,
): boolean {
  return (startedIn ?? null) === active;
}

/** The refusal copy for a connect refused by {@link startedInActiveWorkspace}. */
export function connectRefusalMessage(startedIn: string | null | undefined): string {
  return startedIn ? CONNECT_WRONG_WORKSPACE_MESSAGE : CONNECT_UNKNOWN_WORKSPACE_MESSAGE;
}

/**
 * True only when the completion is provably for `activeWorkspaceId`.
 *
 * FAILS CLOSED on both unknowns, and both are deliberate:
 *
 *  - A completion with NO `workspaceId` is refused rather than trusted. Such a record can
 *    only come from a tab that stashed it before this field shipped, so the window is one
 *    deploy wide and the cost of refusing is a single retry; the cost of trusting it is
 *    the whole defect this exists to close, because a legacy record is exactly what an
 *    attack would arrange to look like. `returnTo` reasons the other way (absent means
 *    "fall back to the connections page") and that is right for a navigation hint and
 *    wrong for a security boundary.
 *  - A null `activeWorkspaceId` is refused too: a consumer that has not resolved one yet
 *    cannot vouch for anything. Unlike {@link startedInActiveWorkspace}'s `null → null`,
 *    two unknowns here are NOT the same unknown: this record survived a full page
 *    navigation through `/plaid-oauth`, and the stored workspace it would be compared
 *    against is exactly what a second tab could have rewritten in the meantime.
 *
 * Callers must DISCARD a refused completion rather than leave it in storage. Leaving it
 * turns one refusal into a record that gets re-offered under every workspace the user
 * visits next, which is the same defect with more attempts.
 */
export function completionMatchesWorkspace(
  completion: PlaidCompletion,
  activeWorkspaceId: string | null,
): boolean {
  if (!completion.workspaceId || !activeWorkspaceId) return false;
  return completion.workspaceId === activeWorkspaceId;
}

/** The refusal copy for a completion `completionMatchesWorkspace` rejected. Branches on
 *  `kind` as well as on what we know: see the copy block above. */
export function completionRefusalMessage(completion: PlaidCompletion): string {
  if (completion.kind === 'relink') {
    return completion.workspaceId ? RELINK_WRONG_WORKSPACE_MESSAGE : RELINK_UNKNOWN_WORKSPACE_MESSAGE;
  }
  return connectRefusalMessage(completion.workspaceId);
}

/**
 * A failed OAuth-resume Link exit (F3), handed from `/plaid-oauth` back to
 * ConnectedAccounts the same way a completion is — that page navigates away as soon
 * as `onExit` fires, so it cannot show an inline error itself; it stashes the
 * already-built plain-language message here and ConnectedAccounts surfaces it on
 * the next render, alongside picking up any completion.
 */
export function saveExitError(message: string): void { writeJson(sessionStorage, EXIT_ERROR_KEY, message); }
/** Read AND remove the pending exit error — it must be consumed exactly once. */
export function takeExitError(): string | null {
  const v = readJson<string>(sessionStorage, EXIT_ERROR_KEY);
  remove(sessionStorage, EXIT_ERROR_KEY);
  return v;
}
